/**
 * The product in more than one language.
 *
 * WHAT IS TRANSLATED, AND WHAT IS NOT. The customer-facing booking
 * experience is: a business's public page, the booking flow, the
 * confirmation, the page a customer manages or cancels an appointment
 * on, and — since migration 0039 — the emails and text messages that
 * appointment sends them. Those are what `OFFERED_LOCALES` promises, and
 * the language control is shown on the screens among them. NOT
 * translated, and therefore never offering a choice: the owner's admin
 * console and the marketing site. Each is a separate piece of work with
 * its own strings.
 *
 * A MESSAGE HAS NOBODY TO ASK. A page can read the language out of the
 * browser in front of it; a reminder sent at six in the morning cannot.
 * That is why 0039 writes the language a person booked in against the
 * customer, and why the templates below are rendered from it rather than
 * from anything in a request.
 *
 * THAT DISTINCTION IS THE WHOLE DESIGN. A person who picks Español and
 * then reads "Choose a service" has been told their language is
 * supported when it is not, so:
 *
 *   - `t()` falls back to English key by key, so a missing translation
 *     is a readable English sentence rather than a blank or a raw key;
 *   - `OFFERED_LOCALES` lists only languages whose booking strings are
 *     complete, and the switcher renders nothing when there is one;
 *   - `tests/unit/i18n.test.ts` fails if an offered language is missing
 *     any key, so a string added in English cannot quietly ship as a
 *     hole in Spanish.
 *
 * TWO SETTINGS, NOT ONE. A business chooses the language its own page is
 * published in (`booking_pages.locale`, migration 0038). A visitor
 * chooses the language they read in, which wins, and lives in their
 * browser. Neither overrides the other's meaning: the business's choice
 * is the page's default, the visitor's is their own.
 */

export const LOCALES = ['en', 'es'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

/** How each language names itself, for the switcher. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
};

/**
 * The languages a person may choose, because the booking strings below
 * are complete in them. Adding one means translating every key and
 * widening the check constraint in a migration.
 */
export const OFFERED_LOCALES: readonly Locale[] = ['en', 'es'];

/** The tag handed to Intl for dates, times and money. */
export const INTL_LOCALES: Record<Locale, string> = {
  en: 'en-US',
  es: 'es-US',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

export function localeIsOffered(locale: Locale): boolean {
  return OFFERED_LOCALES.includes(locale);
}

export function intlLocale(locale: Locale): string {
  return INTL_LOCALES[locale] ?? INTL_LOCALES.en;
}

/**
 * English is the source. Keys are dotted and say WHERE the string is, so
 * a translator can see what they are translating.
 */
const EN = {
  // The language control itself.
  'language.choose': 'Language',
  'language.changed': 'Language changed to {language}.',

  'appearance.control': 'Appearance',
  'appearance.theme': 'Theme',
  'appearance.light': 'Light',
  'appearance.dark': 'Dark',
  'appearance.reduce_motion': 'Reduce motion',
  'appearance.use_device': 'Use device setting',
  'appearance.following_device': 'Following your device setting',
  // The business page shell.
  'page.loading': 'Loading…',
  'page.loading_appointment': 'Loading your appointment…',
  'page.loading_appointments': 'Loading your appointments…',
  'page.loading_invitation': 'Checking your invitation…',
  'page.something_wrong': 'Something went wrong',
  'page.try_again': 'Try again',
  'page.not_found_title': 'Page not found',
  'page.not_found_message': 'The address may be wrong, or this business has not published its booking page yet.',
  'page.back_to_start': 'Back to start',
  'page.booking_not_open_title': 'Online booking is not open yet',
  'page.booking_not_open_message': 'This business will be taking online bookings soon. Please check back later.',
  'page.unavailable_title': 'Temporarily not taking online bookings',
  'page.unavailable_message': "{name} isn't accepting online bookings at the moment. Please get in touch directly and we'll be happy to help.",
  'page.coming_soon_title': 'Online booking is coming soon',
  'page.coming_soon_message': "{name} isn't taking online bookings just yet. Contact us directly to make an appointment.",
  'page.this_business': 'This business',
  'page.powered_by': 'Powered by Book with Assemble',
  'page.contact_details': 'Contact details',
  'page.what_we_do': 'What this business does',
  'page.shop_nav': 'Order from this business',
  'page.shop_link': 'Order products, custom orders and rentals',
  'page.order_now': 'Order now',
  'page.order_now_title': 'Order from {name}',
  'page.order_now_lead': 'Choose from the menu, then collect it as soon as it is ready or at a time that suits you.',
  'page.order_now_book_note': 'Prefer to book a time with us instead? Appointments are below.',
  'page.all_of': 'All of {name}',
  'page.title_book_with': '{name} | Book with Assemble',
  'page.reserved_not_found_message': 'There is nothing at this address.',
  'legal.policies': 'Policies',
  'legal.index_title': 'Policies',
  'legal.index_intro': 'The documents that govern using Book with Assemble.',
  'legal.effective': 'Effective {date} · version {version}',
  'legal.not_found_title': 'Policy not found',
  'legal.not_found_message': 'There is no published policy at this address.',
  'legal.all_policies': 'All policies',
  'legal.loading': 'Loading the policy…',

  // Good to know.
  'policies.heading': 'Good to know',
  'policies.booking': 'Booking policy',
  'policies.how_to_pay': 'How to pay',
  'policies.cancellation': 'Cancellation policy',

  // The steps of a booking.
  'booking.heading': 'Book an appointment',
  'booking.step_location': 'Location',
  'booking.step_provider': 'Book with',
  'booking.step_date_time': 'Date & time',
  'booking.booked_announcement': 'Your appointment is booked.',
  'booking.time_just_taken': 'That time was just taken. Please choose another.',
  'booking.cannot_complete': 'We could not complete this booking online. Please contact the business directly.',
  'booking.connection_error': 'Something went wrong. Please check your connection and try again.',
  'booking.no_services_now': 'No services are available for online booking right now.',
  'booking.choose_location_heading': 'Choose a location',
  'booking.any_available': 'Any available',
  'booking.any_available_desc': "See every open time; we'll match you with whoever is free.",
  'booking.pick_date_time': 'Pick a date and time',
  'booking.any_available_provider': 'Anyone available',
  'booking.any_available_provider_inline': 'anyone available',
  'booking.deposit_note': '{amount} deposit',
  'booking.dismiss': 'Dismiss',
  'booking.change_time': 'Change time',
  'booking.full_name': 'Full name',
  'booking.email_hint': "We'll send your confirmation and a link to manage the appointment here.",
  'booking.phone_label': 'Phone',
  'booking.optional_marker': '(optional)',
  'booking.sms_opt_in': 'Yes, Book with Assemble may text me confirmations, reminders, changes and cancellations for my appointments with this business. Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase. We will not share mobile information with third parties for promotional or marketing purposes.',
  'booking.sms_consent_links': 'See our',
  'booking.sms_consent_privacy': 'Privacy Policy',
  'booking.sms_consent_terms': 'Terms',
  'booking.sms_consent_info': 'SMS information',
  'booking.sms_consent_and': 'and',
  'booking.get_in_touch': "Get in touch and we'll sort it out:",
  'booking.booking_in_progress': 'Booking…',
  'booking.continue_checkout': 'Continue to secure checkout',
  'booking.times_in_your_zone': 'Times are in {zone} (your time).',
  'booking.complete_your_booking': 'Complete your booking',
  'booking.youre_booked': "You're booked",
  'booking.pending_confirmation': 'Your request is pending confirmation from the business.',
  'booking.confirmation_on_way': 'A confirmation is on its way to your email.',
  'booking.manage_this': 'Manage this appointment',
  'booking.keep_manage_link': 'Keep the manage link from your email: it lets you reschedule or cancel. Booked for {when}.',
  'booking.steps_label': 'Booking steps',
  'booking.step_service': 'Service',
  'booking.step_when': 'When',
  'booking.step_details': 'Your details',
  'booking.step_confirm': 'Confirm',
  'booking.step_done': 'Done',
  'booking.choose_service': 'Choose a service',
  'booking.choose_class': 'Choose a class',
  'booking.choose_time': 'Choose a time',
  'booking.your_details': 'Your details',
  'booking.no_services': 'This business has not published any services yet.',
  'booking.service_from': 'from {price}',
  'booking.with_anyone': 'Anyone available',
  'booking.with_provider': 'with {name}',
  'booking.choose_professional': 'Who would you like to see?',
  // Provider preference (0051): a wish about who provides the service,
  // offered only by businesses that switched it on. Never a question
  // about the customer.
  'booking.step_preference': 'Preference',
  'booking.preference_heading': 'Do you have a preference about who provides the service?',
  'booking.preference_hint': 'You can also pick a person by name on the next step when you have no preference.',
  'booking.preference_no_preference': 'No preference',
  'booking.preference_female': 'Female provider',
  'booking.preference_male': 'Male provider',
  'booking.preference_inline': 'Preference: {preference}',
  'booking.preference_none_available': 'Nobody who offers this service matches that preference at the moment.',
  'booking.preference_choose_none': 'Continue with no preference',
  'booking.preference_choose_time': 'Or choose another day.',
  'booking.preference_taken': 'Nobody matching your preference is free at that time. Choose another time, or continue with no preference.',
  'booking.choose_location': 'Where?',
  'booking.back': 'Back',
  'booking.next': 'Next',
  'booking.continue': 'Continue',
  'booking.confirm': 'Confirm booking',
  'booking.confirming': 'Confirming…',
  'booking.name': 'Your name',
  'booking.email': 'Email',
  'booking.phone': 'Mobile number',
  'booking.notes': 'Anything we should know?',
  'booking.notes_hint': 'Optional.',
  'booking.required': 'Required',
  'booking.confirmed_title': 'Your appointment is confirmed',
  'booking.pending_title': 'Almost there',
  'booking.confirmed_message': 'We have sent the details to you. You can change or cancel this appointment from the link in that message.',
  'booking.add_to_calendar': 'Add to calendar',
  'booking.manage_appointment': 'Change or cancel',
  'booking.book_another': 'Book another appointment',
  'booking.no_times_title': 'No times available',
  'booking.no_times': 'No times are available in this period. Try another date or another professional.',
  'booking.times_in_zone': 'Times are shown in {zone}, your own time zone.',
  'booking.taken_title': 'That time has just been taken',
  'booking.taken_message': 'Somebody booked it while you were filling this in. Please choose another time.',
  'booking.checking_details': 'Checking your booking details…',
  'booking.total_heading': 'Your booking total',
  'booking.service_total': 'Service total: {amount}',
  'booking.due_now': 'Due now: {amount}',
  'booking.remaining': 'Remaining: {amount}',
  'booking.accept_terms': 'I understand and accept these payment, cancellation and no show terms.',
  'booking.cannot_take_payment': 'This business cannot accept the required online payment yet. Please contact them before booking.',
  'booking.member_included': 'Service included',
  'booking.member_saving': '{amount} member saving',
  'booking.regular_price': 'Regular price {amount}.',
  'booking.terms_error': 'Could not load booking terms. Please try again.',
  // Dual pricing (0044): two real prices, shown together, before anybody
  // chooses. Never a fee added at the end.
  'booking.cash_price': 'Cash discount price',
  'booking.card_price': 'Card price',
  'booking.how_to_pay_heading': 'How would you like to pay?',
  'booking.pay_cash_option': 'Use the cash discount price with cash, Zelle or the business’s local terminal',
  'booking.pay_card_option': 'Pay the card price online through Book with Assemble',
  'booking.cash_saving_note': 'This business offers a lower cash discount price for cash, Zelle or a card taken on its own local terminal.',
  'booking.you_chose_cash': 'You are booking at the cash discount price.',
  'booking.you_chose_card': 'You are booking at the card price.',
  'booking.price_updating': 'Updating your price…',
  'tiles.choose_price_heading': 'Choose the cash discount price or card price',
  'tiles.switch_to_cash': 'Use the cash discount price',
  'tiles.switch_to_card': 'Use the card price',
  'tiles.price_changed': 'Your price is now {amount}.',
  'tiles.price_change_error': 'We could not change your payment method. Please try again.',
  // Card processing fee (0048): the price is the price; a credit card
  // adds a disclosed fee, shown as its own line before anybody pays.
  'booking.price': 'Price',
  'booking.card_fee': 'Card processing fee ({percent}%, credit cards)',
  'booking.credit_card_total': 'Credit card total',
  'booking.card_fee_note': 'Paying with a credit card adds a card processing fee of {percent}%. Cash, debit and prepaid cards pay the price shown.',
  'booking.card_fee_short': 'Credit card {amount} (includes {percent}% card processing fee)',

  // What, when, where.
  'summary.when': 'When',
  'summary.service': 'Service',
  'summary.where': 'Where',
  'summary.your_time': '{zone} (your time)',
  'summary.business_time': '{when} in {zone} (the business’s time)',
  'summary.with': 'with {name}',

  'pay.cash': 'Cash',
  'pay.card_in_person': 'Card at your appointment',
  'pay.zelle_to': 'Zelle to {recipient}',
  'pay.deposit_received': 'Deposit of {amount} received, thank you.',
  'pay.deposit_required': 'A deposit of {amount} is required to hold this appointment.',
  'pay.accepted_methods': 'Accepted payment methods',
  'pay.status_paid': 'Paid',
  'pay.status_waived': 'No payment due',
  'pay.status_card_on_file': 'Card securely saved',
  'pay.status_deposit_paid': 'Deposit paid',
  'pay.status_partially_paid': 'Partially paid',
  'pay.status_refunded': 'Refunded',
  'pay.status_partially_refunded': 'Partially refunded',
  'pay.status_payment_failed': 'Payment failed',
  'pay.status_fee_review': 'Fee under review',
  'pay.status_fee_paid': 'Fee paid',
  // Classes and the waitlist.
  'picker.morning': 'Morning',
  'picker.afternoon': 'Afternoon',
  'picker.evening': 'Evening',
  'picker.times_error': 'Could not load times. Please try again.',
  'picker.choose_day': 'Choose a day',
  'picker.previous_month': 'Previous month',
  'picker.next_month': 'Next month',
  'picker.pick_a_day': 'Pick a day to see available times.',
  'picker.no_times_this_day': 'No times on this day. Try another day.',
  'picker.there': 'there',
  'classes.none_open': 'No {service} classes are open for booking just now.',
  'classes.times_in_zone': 'Times shown in {zone} (your time).',
  'classes.need_name_email': 'Please give your name and email so we can confirm your place.',
  'classes.could_not_book': 'We could not book that place. Please try again.',
  'classes.places_left': '{count} places left',
  'classes.place_left': '1 place left',
  'classes.email_hint': "We'll send your confirmation and a link to manage your place here.",
  'classes.booking_place': 'Booking your place…',
  'classes.book_my_place': 'Book my place',
  'classes.on_at': '{service} on {date} at {time}.',
  'classes.heading': 'Classes',
  'classes.seats_left': '{count} places left',
  'classes.one_seat_left': '1 place left',
  'classes.full': 'Full',
  'classes.join_waitlist': 'Join the waitlist',
  'classes.book_place': 'Book a place',
  'classes.none': 'No classes are scheduled at the moment.',
  'waitlist.need_name_email': 'Please give your name and email so we can reach you.',
  'waitlist.end_after_start': 'Choose an end date on or after the start date.',
  'waitlist.could_not_add': 'We could not add you just now. Please try again.',
  'waitlist.on_the_list': "You're on the list",
  'waitlist.we_will_email': "We'll email {email} if a {service} appointment opens up between {from} and {until}.",
  'waitlist.not_held': "Openings are not held — whoever books first takes them, so it's worth being quick.",
  'waitlist.back_to_times': 'Back to times',
  'waitlist.tell_me': 'Tell me when something opens',
  'waitlist.from': 'From',
  'waitlist.until': 'Until',
  'waitlist.adding': 'Adding you…',
  'waitlist.add_me': 'Add me to the list',
  'waitlist.cancel': 'Cancel',
  'waitlist.heading': 'Join the waitlist',
  'waitlist.explain': 'Tell us when suits you. We will contact you if a place opens.',
  'waitlist.submit': 'Join the waitlist',
  'waitlist.joined': 'You are on the waitlist. We will be in touch if something opens up.',
  'waitlist.earliest': 'Earliest date',
  'waitlist.latest': 'Latest date',
  'waitlist.any_time': 'Any time of day',
  'waitlist.morning': 'Mornings',
  'waitlist.afternoon': 'Afternoons',
  'waitlist.evening': 'Evenings',

  'tiles.copied': 'Copied',
  'tiles.copy': 'Copy',
  'tiles.payment': 'Payment',
  'tiles.arrange_directly': 'Arrange payment with the business directly.',
  'tiles.payment_options': 'Payment options',
  'tiles.due_at_appointment': 'due at your appointment',
  'tiles.due_now_deposit': 'due now as a deposit',
  'tiles.due_now': 'due now',
  'tiles.waiting_confirm': 'Waiting for the business to confirm {amount}.',
  'tiles.nothing_outstanding': 'Nothing outstanding — thank you.',
  'tiles.you_told_them': 'You told the business you sent {amount}. They will confirm it once it appears in their account.',
  'tiles.cancellation': 'Cancellation:',
  'tiles.refunds': 'Refunds:',
  'tiles.ways_to_pay': 'Ways to pay',
  'tiles.send_with_zelle': 'Send with Zelle',
  'tiles.to': 'To',
  'tiles.zelle': 'Zelle',
  'tiles.amount': 'Amount',
  'tiles.reference': 'Reference',
  'tiles.zelle_qr_alt': 'Zelle QR code for {name}',
  'tiles.this_business': 'this business',
  'tiles.send_then_tell': 'Send it in your own banking app, then tell the business below. They will confirm it when it arrives — Assemble cannot see your bank.',
  'tiles.bank_reference': 'Reference from your bank',
  'tiles.claim_recorded': 'Thank you — the business has been told and will confirm it.',
  'tiles.claim_error': 'We could not record that. Please try again.',
  'tiles.telling_them': 'Telling them…',
  'tiles.i_sent_it': 'I sent the payment',
  'tiles.taken_by_business': '{method} is taken by the business. Nothing to do here.',
  'tiles.taken_at_appointment': '{method} is taken by the business at your appointment. Nothing to do here.',
  'tiles.taken_by_provider': '{method} is taken securely by the business’s payment provider.',
  // Managing an appointment.
  'manage.status_pending': 'Pending confirmation',
  'manage.status_confirmed': 'Confirmed',
  'manage.status_cancelled': 'Cancelled',
  'manage.status_completed': 'Completed',
  'manage.status_no_show': 'Missed',
  'manage.status_rescheduled': 'Rescheduled',
  'manage.missing_code': 'This link is missing its access code. Please open the link from your confirmation email.',
  'manage.not_found': "We couldn't find this appointment. The link may be incorrect or no longer valid.",
  'manage.load_error': 'Could not load your appointment. Please try again.',
  'manage.title': 'Your appointment',
  'manage.title_with_business': 'Your appointment · {business}',
  'manage.appointment_word': 'Appointment',
  'manage.go_to_booking_page': 'Go to the booking page',
  'manage.finish_checkout': 'Finish secure checkout',
  'manage.time_reserved': 'Your time is temporarily reserved. Payment confirmation will appear here after the provider verifies it.',
  'manage.continue_checkout': 'Continue secure checkout',
  'manage.refresh_payment': 'Refresh payment status',
  'manage.checkout_error': 'Could not start checkout.',
  'manage.accepted_terms': 'Accepted booking terms',
  'manage.confirm_cancel': 'Cancel this appointment? This cannot be undone.',
  'manage.was_cancelled': 'Your appointment has been cancelled.',
  'manage.cancel_error': 'Could not cancel. Please try again.',
  'manage.was_moved': 'Your appointment has been moved.',
  'manage.reschedule_error': 'Could not reschedule. Please try again.',
  'manage.moved_check_email': 'This appointment was moved. Check your email for the link to the new time.',
  'manage.changes_closed': 'Online changes close {notice} before the start time, so this appointment can no longer be changed here. Please contact the business directly.',
  'manage.change_until': 'You can reschedule or cancel online until {when}.',
  'manage.reschedule_button': 'Reschedule',
  'manage.working': 'Working…',
  'manage.choose_new_time': 'Choose a new time',
  'manage.keep_current_time': 'Keep current time',
  'manage.moving': 'Moving…',
  'manage.confirm_new_time': 'Confirm new time',
  'manage.other_appointments': 'Your other appointments here',
  'manage.need_help': 'Need help?',
  'manage.book_another': 'Book another appointment',
  // Deskless self check-in (0043). The customer's side of it: the button
  // they press on arrival, and every way it can refuse them.
  'checkin.heading': "You're here?",
  'checkin.button': "I'm here",
  'checkin.working': 'Checking you in…',
  'checkin.done': "You're checked in. We'll let the team know you've arrived.",
  'checkin.done_at': 'Checked in at {when}.',
  'checkin.too_early': 'You can check in from {when}.',
  'checkin.closed': 'Check-in has closed for this appointment. Please speak to us.',
  'checkin.locating': 'Finding your location…',
  'checkin.needs_location': 'Check in when you arrive — we need your location to confirm you are here.',
  'checkin.location_denied': 'We could not read your location. Allow location access and try again, or check in at the front desk.',
  'checkin.too_far': 'You look too far away to check in. Try again once you arrive.',
  'checkin.front_desk': 'Please check in at the front desk.',
  'checkin.error': 'We could not check you in. Please speak to us.',
  'manage.stop_texts': 'Stop text reminders',
  'manage.texts_stopped': 'Text reminders stopped for your current bookings with this business.',
  'manage.preference_error': 'Could not update your preference.',
  'manage.heading': 'Your appointment',
  // Confirming from the email (0042). The email's button lands HERE and
  // this button is what actually records it, because a mail client
  // fetching a link is not a person pressing one.
  'manage.confirm_heading': 'Are you coming?',
  'manage.confirm_ask': '{business} would like to know you will be there.',
  'manage.confirm_button': 'Yes, I will be there',
  'manage.confirming': 'Confirming…',
  'manage.confirmed_thanks': 'Thank you. {business} knows you are coming.',
  'manage.confirmed_on': 'You confirmed this appointment on {when}.',
  'manage.confirm_error': 'Could not confirm. Please try again.',
  'manage.reschedule': 'Change the time',
  'manage.cancel': 'Cancel appointment',
  'manage.cancel_confirm': 'Cancel this appointment?',
  'manage.cancelled': 'This appointment is cancelled.',
  'manage.cancel_reason': 'Reason (optional)',
  'manage.keep': 'Keep my appointment',
  'manage.rescheduled': 'Your appointment has been moved.',
  'manage.link_expired': 'This link is no longer valid. Please contact the business.',
  'manage.too_late_to_cancel': 'It is too late to cancel online. Please contact the business directly.',
  'manage.status': 'Status',
  'manage.payment': 'Payment',

  // The customer's own account.
  'account.sign_in_to_see': 'Sign in to see your appointments',
  'account.sign_in_free': 'Use your mobile number or email address. It is free.',
  'account.load_failed': 'We could not load your appointments',
  'account.try_again_moment': 'Please try again in a moment.',
  'account.cancel_failed': 'We could not cancel that. Please contact the business.',
  'account.which_appointments': 'Which appointments',
  'account.nothing_booked': 'Nothing booked yet. Open a business’s booking link to make an appointment.',
  'account.no_past': 'No past appointments yet.',
  'account.booked': 'Booked',
  'account.book_again': 'Book again',
  'account.cancelling': 'Cancelling…',
  'account.cancel': 'Cancel',
  'account.businesses_you_use': 'Businesses you use',
  'account.sign_in_options': 'Your sign in options',
  'account.only_your_own': 'Your account only opens your own appointments. Your businesses each have their own booking page.',
  'account.contact_verified': 'Contact verified. Your appointments have been updated.',
  'account.add_verified_contact': 'Add a verified email or mobile number',
  'account.use_known_contact': 'Use the contact your business knows to connect your existing appointments.',
  'account.verify_contact': 'Verify contact',
  'account.try_again': 'Please try again.',
  'account.connect_for_next_time': 'Connect an account for next time.',
  'account.connect': 'Connect',
  'account.continue_with': 'Continue with',
  'account.or_use_email': 'or use email or mobile',
  'invite.error': 'We could not accept that invitation. Please try again.',
  'invite.no_longer_usable': 'This invitation is no longer usable',
  'invite.once_and_expire': 'Invitations can only be used once, and they expire. Ask the business for a new one.',
  'invite.about': 'About Book with Assemble',
  'invite.hello': 'Hello {name}',
  'invite.explain': '{business} has invited you to see your appointments with them online — to book, rebook and cancel whenever you like. It is free, and only you will see your own appointments.',
  'invite.sign_in_then_return': 'Sign in, then come back',
  'invite.connecting': 'Connecting…',
  'invite.yes_connect': 'Yes, connect me',
  'invite.only_if_you_are': 'Only press this if you are {name} and you asked {business} for it.',
  'signin.eyebrow': 'WELCOME TO BOOK WITH ASSEMBLE',
  'signin.title': 'Your time. Your appointments.',
  'signin.subtitle': 'One account for the businesses you love.',
  'signin.link_required': 'Sign in with an email or mobile code first, then connect your provider from your account.',
  'signin.social_failed': 'That sign in did not finish. Please try again or use a code below.',
  'signin.enter_destination': 'Enter your mobile number or email address.',
  'signin.throttled': 'That has been asked for a few times already. Please wait a few minutes and try again.',
  'signin.generic_error': 'Something went wrong. Please try again.',
  'signin.enter_six_digits': 'Enter the six digits we sent you.',
  'signin.code_failed': 'That code did not work. Ask for a new one.',
  'signin.destination_label': 'Mobile number or email',
  'signin.name_label': 'Your name',
  'signin.name_first_time': '(only if this is your first time)',
  'signin.sending': 'Sending…',
  'signin.send_code': 'Send me a code',
  'signin.code_label': 'Six-digit code',
  'signin.checking': 'Checking…',
  'signin.send_another': 'Send another code',
  'signin.different_number': 'Use a different number',
  'signin.free_note': 'Booking and managing appointments is free.',
  'signin.what_is_this': 'What is Book with Assemble?',
  'signin.code_sent': 'We sent a six-digit code to {masked}. It expires in ten minutes.',
  'signin.sms_not_configured': 'Text messages are not switched on here yet, so no code could be sent to that number. Use an email address instead, or ask the business to invite you.',
  'signin.email_not_configured': 'Email is not switched on here yet, so no code could be sent. Ask the business to invite you.',
  'signin.send_failed': 'We could not send the code just now. Please try again in a moment.',
  'account.heading': 'Your appointments',
  'account.upcoming': 'Upcoming',
  'account.past': 'Past',
  'account.none_upcoming': 'You have no upcoming appointments.',
  'account.none_past': 'Nothing here yet.',
  'account.sign_in': 'Sign in',
  'account.sign_out': 'Sign out',
  'account.send_code': 'Send code',
  'account.code_label': 'Six digit code',
  'account.verify': 'Verify',
  'account.email_or_mobile': 'Email or mobile number',
  'account.please_wait': 'Please wait…',
  'account.use_another': 'Use another contact',
  'account.your_businesses': 'Businesses you book with',

  'member.heading': 'Memberships and rebooking reminders',
  'member.reminder_opt_in': 'Email me an occasional reminder when I may be due for another visit. Optional; unsubscribe any time.',
  'member.preference_error': 'Could not update preference.',
  'member.at_business': 'Memberships at {business}',
  'member.load_error': 'Could not load memberships.',
  'member.checkout_error': 'Could not start checkout.',
  'member.cancel_error': 'Could not cancel membership.',
  'member.period_ends': 'Paid period ends {date}',
  'member.will_not_renew': 'Will not renew',
  'member.resume_checkout': 'Resume secure checkout',
  'member.cancel': 'Cancel membership',
  'member.cancel_confirm_immediate': 'Cancel {name}? Benefits end immediately.',
  'member.cancel_confirm_period_end': 'Cancel {name}? Benefits continue until the paid period ends.',
  'member.price_every': '{price} every {count} {interval}',
  'member.unlimited': 'Unlimited eligible services',
  'member.included_per_period': '{count} eligible services per paid period',
  'member.discount_after': '{percent}% member discount after included services',
  'member.eligible_services': 'Eligible services: {services}',
  'member.renews_immediate': 'Renews automatically. Cancel with immediate effect. Credits apply within the paid period and do not roll over.',
  'member.renews_period_end': 'Renews automatically. Cancel at the end of your paid period. Credits apply within the paid period and do not roll over.',
  'member.accept_terms': 'I understand the recurring price and cancellation terms.',
  'member.join': 'Join with secure checkout',
  // Words that appear in more than one place.
  'common.optional': 'Optional',
  'common.save': 'Save',
  'common.close': 'Close',
  'common.loading': 'Loading…',
  'common.duration_minutes': '{count} min',
  'common.duration_hour': '1 hr',
  'common.duration_hours': '{count} hrs',
  'common.duration_hours_minutes': '{hours} hr {minutes} min',

  // Email and text message templates (migration 0039). These are the
  // only strings in this catalogue nobody reads on a screen: they are
  // rendered on the server, in the language the customer booked in, and
  // sent when there is no browser to ask. A business's own words — its
  // name, services, policies, payment instructions and confirmation note
  // — pass through exactly as written, in every language.
  'notify.hi': 'Hi {name},',
  'notify.greeting_confirmation': 'Hi {name}, your appointment is confirmed.',
  'notify.greeting_reminder': 'Hi {name}, a reminder about your upcoming appointment.',
  'notify.greeting_reschedule': 'Hi {name}, your appointment has been rescheduled.',
  'notify.greeting_cancellation': 'Hi {name}, your appointment has been cancelled.',
  'notify.service_with_provider': '{service} with {provider}',
  'notify.also_at_location': ' ({when} at the location)',
  'notify.change_it': 'Need to change it? {url}',
  'notify.cancellation_policy': 'Cancellation policy: {policy}',
  'notify.payment': 'Payment: {details}',
  'notify.pay_by': 'pay by {methods}',
  'notify.method_cash': 'cash',
  'notify.method_card_in_person': 'card at your appointment',
  'notify.method_zelle': 'Zelle to {recipient}',
  'notify.deposit_received': 'Deposit of {amount}: received, thank you.',
  'notify.deposit_required': 'A deposit of {amount} is required to hold this appointment.',
  'notify.questions': 'Questions? {contact}',
  'notify.subject': '{summary} — {business}',
  'notify.subject_confirmation': 'Confirmed: {service} on {when}',
  'notify.subject_reminder': 'Reminder: {service} on {when}',
  'notify.subject_reschedule': 'Rescheduled: {service} now on {when}',
  'notify.subject_cancellation': 'Cancelled: {service} on {when}',
  'notify.review.subject': 'How was your visit to {business}?',
  'notify.review.body': 'Thank you for choosing {business}. If you have a moment, a short review helps other people find us:',
  'notify.waitlist.subject': 'A time opened up at {business}: {service} on {when}',
  'notify.waitlist.there': 'there',
  'notify.waitlist.body': 'You asked to hear when {business} had an opening. One has just come free:',
  'notify.waitlist.not_held': 'It is not held for you — whoever books first takes it, so it is worth being quick.',
  'notify.waitlist.book_here': 'Book it here: {url}',
  'notify.rebooking.subject': 'Ready for another visit to {business}?',
  'notify.rebooking.body': 'Your last {service} appointment was on {when}. Would you like to book again?',
  'notify.rebooking.why': 'You asked for rebooking reminders from {business}.',
  'notify.rebooking.stop': 'Stop rebooking emails: {url}',
  'notify.staff.booked': 'A customer booked online.',
  'notify.staff.cancelled': 'A customer cancelled online.',
  'notify.staff.checked_in': '{name} has arrived and checked in.',
  'notify.staff.customer': 'Customer: {name}',
  'notify.staff.deposit_asked': 'Deposit asked: {amount}',
  'notify.staff.deposit_received': 'Deposit asked: {amount} (received)',
  'notify.staff.subject_new': 'New booking: {what}, {when}',
  'notify.staff.subject_cancelled': 'Cancelled: {what}, {when}',
  'notify.staff.subject_checked_in': 'Checked in: {name}, {when}',
  'notify.sms.confirmed': 'Your appointment with {business} is confirmed for {date} at {time}.',
  'notify.sms.reminder': 'Reminder: your appointment with {business} is {date} at {time}.',
  'notify.sms.rescheduled': 'Your appointment with {business} has been moved to {date} at {time}.',
  'notify.sms.cancelled': 'Your appointment with {business} on {date} at {time} has been cancelled.',
  // The closing line, the buttons and the confirmation ask (0042). A
  // business may replace any of them with its own words; these are what
  // it gets without doing anything, in the customer's own language.
  'notify.closing': 'See you soon!',
  'notify.button_confirm': 'Confirm this appointment',
  'notify.button_details': 'See the details',
  'notify.button_book': 'Book an appointment',
  'notify.button_cancel': 'Cancel this appointment',
  'notify.confirm_ask': 'Please confirm so {business} knows to expect you.',
  'notify.details_heading': 'Your appointment',
  'notify.label_when': 'When',
  'notify.label_where': 'Where',
  'notify.label_what': 'What',
  'notify.plain_links': 'If the buttons do not work, open this link:',

  // Booking invitation (0051): sent only when the business asks, after
  // bringing its customers in. A link to book, and nothing to buy.
  'notify.invitation.subject': 'Book your next appointment with {business} online',
  'notify.invitation.body': '{business} now takes bookings online. Pick a time that suits you, any time of day, and get a confirmation straight away.',
  'notify.invitation.fine': 'You are receiving this because you are a customer of {business}. There is nothing to do if you would rather keep booking the way you do now.',
  'notify.sms.invitation': '{business}: you can now book your next appointment online at {url}',

  // Intake forms (0041). The customer's side only: the owner's console
  // that builds these forms is English, like the rest of it. A
  // business's own words — the form's name, its questions, its
  // acknowledgments and its privacy sentence — pass through exactly as
  // written, in every language.
  'form.heading': 'Before your visit',
  'form.progress': '{answered} of {total} answered',
  'form.required': 'Required',
  'form.optional': 'Optional',
  'form.required_note': 'Questions marked required must be answered.',
  'form.submit': 'Send my answers',
  'form.sending': 'Sending…',
  'form.fix_errors': 'Please check the highlighted questions.',
  'form.yes': 'Yes',
  'form.no': 'No',
  'form.choose': 'Choose one',
  'form.choose_any': 'Choose any that apply',
  'form.signature_label': 'Type your full name',
  'form.initials_label': 'Type your initials',
  'form.attach': 'Attach a file',
  'form.attaching': 'Uploading…',
  'form.attached': 'Attached: {name}',
  'form.attach_hint': 'JPEG, PNG, WebP or PDF, up to 5 MB. Stored privately.',
  'form.confirm_unchanged': 'Nothing has changed since I last completed this form.',
  'form.confirm_last_completed': 'You last completed this on {date}.',
  'form.confirm_button': 'Confirm nothing has changed',
  'form.done_title': 'Thank you — we have your answers',
  'form.done_body': 'There is nothing else to do. You can close this page.',
  'form.expired_title': 'This link has expired',
  'form.expired_body': 'Please ask the business to send you a new one.',
  'form.completed_title': 'This form is already complete',
  'form.completed_body': 'Thank you — there is nothing else to do.',
  'form.not_found_title': 'This form could not be found',
  'form.not_found_body': 'The link may be wrong or it may have been replaced by a newer one.',
  'form.expires_on': 'This link works until {date}.',
  'form.privacy_heading': 'Your privacy',
  'form.for_appointment': 'For your {service} appointment on {when}.',

  // Service requests (0053): the card on the public page, and what the
  // customer is told. The business's own questions pass through as
  // written.
  'request.heading': 'Request a service',
  'request.intro': 'Tell us what you need and how to reach you. No appointment needed — we will be in touch.',
  'request.start': 'Start a request',
  'request.about_you': 'About you',
  'request.name': 'Your name',
  'request.phone': 'Phone number',
  'request.email': 'Email',
  'request.contact_note': 'We need a phone number or an email address to reach you.',
  'request.submit': 'Send my request',
  'request.sending': 'Sending…',
  'request.done_title': 'Request sent',
  'request.done_body': '{name} has your request and will be in touch.',
  'request.another': 'Send another request',
  'request.not_available': 'This business is not taking requests online right now.',
  'notify.request.subject_received': 'We have your request — {business}',
  'notify.request.received_body': 'Thank you — {business} has your request and will be in touch.',
  'notify.request.subject_update': 'Your request with {business}: {status}',
  'notify.request.update_body': 'Your request with {business} is now {status}.',
  'notify.request.completed_body': 'Your request with {business} is complete.',
  'notify.request.cancelled_body': 'Your request with {business} has been cancelled.',
  'notify.request.label_request': 'Your request',
  'notify.request.label_address': 'Service address',
  'notify.request.label_needed': 'Needed by',
  'notify.request.sms_received': '{business}: we have your request and will be in touch.',
  'notify.request.sms_update': '{business}: your request is now {status}.',
  'notify.request.status_new': 'received',
  'notify.request.status_contacted': 'in touch',
  'notify.request.status_in_progress': 'in progress',
  'notify.request.status_waiting': 'waiting on a reply',
  'notify.request.status_completed': 'complete',
  'notify.request.status_cancelled': 'cancelled',
  'notify.staff.request_subject': 'New service request: {name}',
  'notify.staff.request_body': '{name} sent a service request from your page.',

  // Kiosk Mode (0056): the tablet at the counter. Short, because the
  // person reading it is standing up.
  'kiosk.title': '{name} | Kiosk',
  'kiosk.pair_title': 'Set up this kiosk',
  'kiosk.pair_hint': 'Ask a manager for the six-digit pairing code from the console, then enter it here.',
  'kiosk.pair_code': 'Pairing code',
  'kiosk.pair_button': 'Connect',
  'kiosk.pair_failed': 'That code is not valid. Ask a manager for a new one.',
  'kiosk.off_title': 'The kiosk is switched off',
  'kiosk.off_message': 'Please speak to the team at the counter.',
  'kiosk.reconnect': 'This kiosk needs to be set up again. Ask a manager for a pairing code.',
  'kiosk.welcome': 'Welcome',
  'kiosk.welcome_to': 'Welcome to {name}',
  'kiosk.tap_to_start': 'Tap to begin',
  'kiosk.have_appointment': 'I have an appointment',
  'kiosk.walk_in': 'Walk in',
  'kiosk.start_order': 'Start order',
  'kiosk.order_at_counter': 'Please order at the counter.',
  'kiosk.start_over': 'Start over',
  'kiosk.back': 'Back',
  'kiosk.next': 'Next',
  'kiosk.skip': 'Skip',
  'kiosk.done': 'Done',
  'kiosk.working': 'One moment…',
  'kiosk.find_title': 'Find your appointment',
  'kiosk.find_hint': 'Enter the mobile number or the name the appointment was booked with.',
  'kiosk.phone': 'Mobile number',
  'kiosk.name': 'Your name',
  'kiosk.find_button': 'Find my appointment',
  'kiosk.not_found_title': 'We could not find an appointment',
  'kiosk.not_found_message': 'Check the number or name, or speak to the team at the counter.',
  'kiosk.matches_title': 'Is this you?',
  'kiosk.check_in': 'Check in',
  'kiosk.too_early': 'A little early — check-in opens at {time}.',
  'kiosk.already_checked_in': 'Already checked in',
  'kiosk.closed': 'Please speak to the team at the counter.',
  'kiosk.with_person': 'with {name}',
  'kiosk.checked_in_title': "You're checked in",
  'kiosk.checked_in_message': 'Thanks, {name}. Take a seat — the team knows you are here.',
  'kiosk.walk_name_title': "What's your name?",
  'kiosk.walk_phone_title': 'Your mobile number (optional)',
  'kiosk.walk_phone_hint': 'So the team can reach you if they need to.',
  'kiosk.walk_service_title': 'What are you here for?',
  'kiosk.walk_service_any': "I'm not sure yet",
  'kiosk.walk_provider_title': 'Anyone in particular?',
  'kiosk.walk_provider_any': 'First available',
  'kiosk.walk_confirm_title': 'Join the line?',
  'kiosk.walk_join': 'Join the line',
  'kiosk.walk_done_title': "You're in line",
  'kiosk.walk_position': 'You are number {position} in line.',
  'kiosk.walk_next': 'You are next.',
  'kiosk.walk_ahead': '{count} ahead of you.',
  'kiosk.walk_done_message': 'Take a seat — the team will call your name.',
  'kiosk.minutes': '{count} min',
  'kiosk.menu_title': 'Menu',
  'kiosk.menu_all': 'All',
  'kiosk.sold_out': 'Sold out',
  'kiosk.add': 'Add',
  'kiosk.add_to_order': 'Add to order',
  'kiosk.quantity': 'Quantity',
  'kiosk.required': 'Required',
  'kiosk.optional': 'Optional',
  'kiosk.choose_one': 'Choose one',
  'kiosk.yes': 'Yes',
  'kiosk.no': 'No',
  'kiosk.view_order': 'View order',
  'kiosk.your_order': 'Your order',
  'kiosk.empty_order': 'Nothing in your order yet.',
  'kiosk.order_notes': 'Anything we should know?',
  'kiosk.order_notes_hint': 'Allergies, no onions, extra napkins…',
  'kiosk.remove': 'Remove',
  'kiosk.total': 'Total',
  'kiosk.continue': 'Continue',
  'kiosk.order_name_title': 'Name for the order',
  'kiosk.order_phone_title': 'Mobile number (optional)',
  'kiosk.place_order': 'Place order',
  'kiosk.pay_at_counter': 'Pay at the counter',
  'kiosk.order_done_title': 'Order placed',
  'kiosk.order_number': 'Your order number',
  'kiosk.order_done_message': 'Please pay at the counter. We will call your name when it is ready.',
  'kiosk.returning_in': 'Back to the start in {seconds}s',
  'kiosk.error_generic': 'Something went wrong. Please try again or speak to the team.',
  'kiosk.each': 'each',
} as const;

export type MessageKey = keyof typeof EN;

/**
 * Spanish. Neutral Latin American Spanish, addressing the customer as
 * "tú" — a hairdresser's booking page is not a bank. A business's own
 * words (its name, its services, its policies) are never translated;
 * only the product's chrome around them is.
 */
const ES: Record<MessageKey, string> = {
  'language.choose': 'Idioma',
  'language.changed': 'Idioma cambiado a {language}.',

  'appearance.control': 'Apariencia',
  'appearance.theme': 'Tema',
  'appearance.light': 'Claro',
  'appearance.dark': 'Oscuro',
  'appearance.reduce_motion': 'Reducir el movimiento',
  'appearance.use_device': 'Usar el ajuste del dispositivo',
  'appearance.following_device': 'Siguiendo el ajuste de tu dispositivo',
  'page.loading': 'Cargando…',
  'page.loading_appointment': 'Cargando tu cita…',
  'page.loading_appointments': 'Cargando tus citas…',
  'page.loading_invitation': 'Revisando tu invitación…',
  'page.something_wrong': 'Algo salió mal',
  'page.try_again': 'Intentar de nuevo',
  'page.not_found_title': 'Página no encontrada',
  'page.not_found_message': 'Puede que la dirección esté mal, o que este negocio aún no haya publicado su página de reservas.',
  'page.back_to_start': 'Volver al inicio',
  'page.booking_not_open_title': 'Las reservas en línea aún no están abiertas',
  'page.booking_not_open_message': 'Este negocio empezará a aceptar reservas en línea muy pronto. Vuelve a intentarlo más tarde.',
  'page.unavailable_title': 'Por ahora no se aceptan reservas en línea',
  'page.unavailable_message': '{name} no está aceptando reservas en línea en este momento. Comunícate directamente y con gusto te atenderemos.',
  'page.coming_soon_title': 'Las reservas en línea llegan pronto',
  'page.coming_soon_message': '{name} todavía no acepta reservas en línea. Comunícate directamente para hacer una cita.',
  'page.this_business': 'Este negocio',
  'page.powered_by': 'Con la tecnología de Book with Assemble',
  'page.contact_details': 'Datos de contacto',
  'page.what_we_do': 'Lo que hace este negocio',
  'page.shop_nav': 'Haz un pedido a este negocio',
  'page.shop_link': 'Pide productos, encargos personalizados y alquileres',
  'page.order_now': 'Pedir ahora',
  'page.order_now_title': 'Haz tu pedido a {name}',
  'page.order_now_lead': 'Elige del menú y recógelo en cuanto esté listo o a la hora que prefieras.',
  'page.order_now_book_note': '¿Prefieres reservar una hora con nosotros? Las citas están más abajo.',
  'page.all_of': 'Todo de {name}',
  'page.title_book_with': '{name} | Book with Assemble',
  'page.reserved_not_found_message': 'No hay nada en esta dirección.',
  'legal.policies': 'Políticas',
  'legal.index_title': 'Políticas',
  'legal.index_intro': 'Los documentos que rigen el uso de Book with Assemble.',
  'legal.effective': 'Vigente desde {date} · versión {version}',
  'legal.not_found_title': 'Política no encontrada',
  'legal.not_found_message': 'No hay ninguna política publicada en esta dirección.',
  'legal.all_policies': 'Todas las políticas',
  'legal.loading': 'Cargando la política…',

  'policies.heading': 'Bueno saberlo',
  'policies.booking': 'Política de reservas',
  'policies.how_to_pay': 'Cómo pagar',
  'policies.cancellation': 'Política de cancelación',

  'booking.heading': 'Reserva una cita',
  'booking.step_location': 'Lugar',
  'booking.step_provider': 'Reservar con',
  'booking.step_date_time': 'Fecha y hora',
  'booking.booked_announcement': 'Tu cita quedó reservada.',
  'booking.time_just_taken': 'Acaban de tomar esa hora. Elige otra, por favor.',
  'booking.cannot_complete': 'No pudimos completar esta reserva en línea. Comunícate directamente con el negocio.',
  'booking.connection_error': 'Algo salió mal. Revisa tu conexión e inténtalo de nuevo.',
  'booking.no_services_now': 'No hay servicios disponibles para reservar en línea en este momento.',
  'booking.choose_location_heading': 'Elige un lugar',
  'booking.any_available': 'Cualquiera disponible',
  'booking.any_available_desc': 'Verás todas las horas libres y te asignamos a quien esté disponible.',
  'booking.pick_date_time': 'Elige la fecha y la hora',
  'booking.any_available_provider': 'Cualquier persona disponible',
  'booking.any_available_provider_inline': 'cualquier persona disponible',
  'booking.deposit_note': '{amount} de depósito',
  'booking.dismiss': 'Descartar',
  'booking.change_time': 'Cambiar la hora',
  'booking.full_name': 'Nombre completo',
  'booking.email_hint': 'Aquí te enviaremos la confirmación y un enlace para administrar la cita.',
  'booking.phone_label': 'Teléfono',
  'booking.optional_marker': '(opcional)',
  'booking.sms_opt_in': 'Sí, Book with Assemble puede enviarme mensajes de texto con confirmaciones, recordatorios, cambios y cancelaciones de mis citas con este negocio. La frecuencia de los mensajes varía. Pueden aplicarse tarifas de mensajes y datos. Responde STOP para darte de baja o HELP para recibir ayuda. El consentimiento no es una condición de compra. No compartiremos información móvil con terceros con fines promocionales o de marketing.',
  'booking.sms_consent_links': 'Consulta nuestra',
  'booking.sms_consent_privacy': 'Política de privacidad',
  'booking.sms_consent_terms': 'Términos',
  'booking.sms_consent_info': 'Información sobre SMS',
  'booking.sms_consent_and': 'y',
  'booking.get_in_touch': 'Comunícate con nosotros y lo resolvemos:',
  'booking.booking_in_progress': 'Reservando…',
  'booking.continue_checkout': 'Continuar al pago seguro',
  'booking.times_in_your_zone': 'Las horas están en {zone} (tu hora).',
  'booking.complete_your_booking': 'Completa tu reserva',
  'booking.youre_booked': 'Tu cita está reservada',
  'booking.pending_confirmation': 'Tu solicitud está pendiente de confirmación por parte del negocio.',
  'booking.confirmation_on_way': 'La confirmación va en camino a tu correo.',
  'booking.manage_this': 'Administrar esta cita',
  'booking.keep_manage_link': 'Guarda el enlace de tu correo: con él puedes cambiar o cancelar la cita. Reservada para {when}.',
  'booking.steps_label': 'Pasos de la reserva',
  'booking.step_service': 'Servicio',
  'booking.step_when': 'Cuándo',
  'booking.step_details': 'Tus datos',
  'booking.step_confirm': 'Confirmar',
  'booking.step_done': 'Listo',
  'booking.choose_service': 'Elige un servicio',
  'booking.choose_class': 'Elige una clase',
  'booking.choose_time': 'Elige una hora',
  'booking.your_details': 'Tus datos',
  'booking.no_services': 'Este negocio aún no ha publicado servicios.',
  'booking.service_from': 'desde {price}',
  'booking.with_anyone': 'Cualquiera disponible',
  'booking.with_provider': 'con {name}',
  'booking.choose_professional': '¿Con quién te gustaría atenderte?',
  'booking.step_preference': 'Preferencia',
  'booking.preference_heading': '¿Tienes alguna preferencia sobre quién te atiende?',
  'booking.preference_hint': 'Si no tienes preferencia, en el siguiente paso también puedes elegir a una persona por su nombre.',
  'booking.preference_no_preference': 'Sin preferencia',
  'booking.preference_female': 'Que me atienda una mujer',
  'booking.preference_male': 'Que me atienda un hombre',
  'booking.preference_inline': 'Preferencia: {preference}',
  'booking.preference_none_available': 'Por ahora nadie que ofrezca este servicio coincide con esa preferencia.',
  'booking.preference_choose_none': 'Continuar sin preferencia',
  'booking.preference_choose_time': 'O elige otro día.',
  'booking.preference_taken': 'Nadie que coincida con tu preferencia está libre a esa hora. Elige otra hora o continúa sin preferencia.',
  'booking.choose_location': '¿Dónde?',
  'booking.back': 'Atrás',
  'booking.next': 'Siguiente',
  'booking.continue': 'Continuar',
  'booking.confirm': 'Confirmar la cita',
  'booking.confirming': 'Confirmando…',
  'booking.name': 'Tu nombre',
  'booking.email': 'Correo electrónico',
  'booking.phone': 'Número de celular',
  'booking.notes': '¿Algo que debamos saber?',
  'booking.notes_hint': 'Opcional.',
  'booking.required': 'Obligatorio',
  'booking.confirmed_title': 'Tu cita está confirmada',
  'booking.pending_title': 'Ya casi',
  'booking.confirmed_message': 'Te enviamos los detalles. Puedes cambiar o cancelar esta cita con el enlace de ese mensaje.',
  'booking.add_to_calendar': 'Agregar al calendario',
  'booking.manage_appointment': 'Cambiar o cancelar',
  'booking.book_another': 'Reservar otra cita',
  'booking.no_times_title': 'No hay horas disponibles',
  'booking.no_times': 'No hay horas disponibles en este periodo. Prueba otra fecha u otro profesional.',
  'booking.times_in_zone': 'Las horas se muestran en {zone}, tu propia zona horaria.',
  'booking.taken_title': 'Acaban de tomar esa hora',
  'booking.taken_message': 'Alguien la reservó mientras llenabas esto. Elige otra hora, por favor.',
  'booking.checking_details': 'Revisando los detalles de tu reserva…',
  'booking.total_heading': 'Total de tu reserva',
  'booking.service_total': 'Total del servicio: {amount}',
  'booking.due_now': 'A pagar ahora: {amount}',
  'booking.remaining': 'Queda por pagar: {amount}',
  'booking.accept_terms': 'Entiendo y acepto estos términos de pago, cancelación y ausencia.',
  'booking.cannot_take_payment': 'Este negocio todavía no puede recibir el pago en línea requerido. Comunícate con ellos antes de reservar.',
  'booking.member_included': 'Servicio incluido',
  'booking.member_saving': '{amount} de ahorro para miembros',
  'booking.regular_price': 'Precio normal {amount}.',
  'booking.terms_error': 'No pudimos cargar los términos de la reserva. Inténtalo de nuevo.',
  'booking.cash_price': 'Precio con descuento por efectivo',
  'booking.card_price': 'Precio con tarjeta',
  'booking.how_to_pay_heading': '¿Cómo prefieres pagar?',
  'booking.pay_cash_option': 'Usar el precio con descuento por efectivo al pagar en efectivo, con Zelle o con la terminal local del negocio',
  'booking.pay_card_option': 'Pagar el precio con tarjeta en línea mediante Book with Assemble',
  'booking.cash_saving_note': 'Este negocio ofrece un precio más bajo con descuento por efectivo para efectivo, Zelle o una tarjeta cobrada en su propia terminal local.',
  'booking.you_chose_cash': 'Estás reservando con el precio con descuento por efectivo.',
  'booking.you_chose_card': 'Estás reservando con el precio con tarjeta.',
  'booking.price_updating': 'Actualizando tu precio…',
  'tiles.choose_price_heading': 'Elige el precio con descuento por efectivo o el precio con tarjeta',
  'tiles.switch_to_cash': 'Usar el precio con descuento por efectivo',
  'tiles.switch_to_card': 'Usar el precio con tarjeta',
  'tiles.price_changed': 'Tu precio ahora es {amount}.',
  'tiles.price_change_error': 'No pudimos cambiar tu forma de pago. Inténtalo de nuevo.',
  'booking.price': 'Precio',
  'booking.card_fee': 'Cargo por procesamiento de tarjeta ({percent}%, tarjetas de crédito)',
  'booking.credit_card_total': 'Total con tarjeta de crédito',
  'booking.card_fee_note': 'Pagar con tarjeta de crédito añade un cargo por procesamiento del {percent}%. En efectivo, con tarjeta de débito o prepagada pagas el precio indicado.',
  'booking.card_fee_short': 'Con tarjeta de crédito {amount} (incluye {percent}% de cargo por procesamiento)',

  'summary.when': 'Cuándo',
  'summary.service': 'Servicio',
  'summary.where': 'Dónde',
  'summary.your_time': '{zone} (tu hora)',
  'summary.business_time': '{when} en {zone} (la hora del negocio)',
  'summary.with': 'con {name}',

  'picker.morning': 'Mañana',
  'picker.afternoon': 'Tarde',
  'picker.evening': 'Noche',
  'picker.times_error': 'No pudimos cargar las horas. Inténtalo de nuevo.',
  'picker.choose_day': 'Elige un día',
  'picker.previous_month': 'Mes anterior',
  'picker.next_month': 'Mes siguiente',
  'picker.pick_a_day': 'Elige un día para ver las horas disponibles.',
  'picker.no_times_this_day': 'No hay horas este día. Prueba otro día.',
  'picker.there': 'allá',
  'pay.cash': 'Efectivo',
  'pay.card_in_person': 'Tarjeta en tu cita',
  'pay.zelle_to': 'Zelle a {recipient}',
  'pay.deposit_received': 'Recibimos tu depósito de {amount}, gracias.',
  'pay.deposit_required': 'Se requiere un depósito de {amount} para reservar esta cita.',
  'pay.accepted_methods': 'Formas de pago aceptadas',
  'pay.status_paid': 'Pagado',
  'pay.status_waived': 'Sin pago pendiente',
  'pay.status_card_on_file': 'Tarjeta guardada de forma segura',
  'pay.status_deposit_paid': 'Depósito pagado',
  'pay.status_partially_paid': 'Pagado en parte',
  'pay.status_refunded': 'Reembolsado',
  'pay.status_partially_refunded': 'Reembolsado en parte',
  'pay.status_payment_failed': 'El pago falló',
  'pay.status_fee_review': 'Cargo en revisión',
  'pay.status_fee_paid': 'Cargo pagado',
  'classes.none_open': 'No hay clases de {service} abiertas para reservar por ahora.',
  'classes.times_in_zone': 'Las horas se muestran en {zone} (tu hora).',
  'classes.need_name_email': 'Dinos tu nombre y tu correo para confirmar tu lugar.',
  'classes.could_not_book': 'No pudimos reservar ese lugar. Inténtalo de nuevo.',
  'classes.places_left': 'Quedan {count} lugares',
  'classes.place_left': 'Queda 1 lugar',
  'classes.email_hint': 'Aquí te enviaremos la confirmación y un enlace para administrar tu lugar.',
  'classes.booking_place': 'Reservando tu lugar…',
  'classes.book_my_place': 'Reservar mi lugar',
  'classes.on_at': '{service} el {date} a las {time}.',
  'classes.heading': 'Clases',
  'classes.seats_left': 'Quedan {count} lugares',
  'classes.one_seat_left': 'Queda 1 lugar',
  'classes.full': 'Llena',
  'classes.join_waitlist': 'Anotarme en la lista de espera',
  'classes.book_place': 'Reservar un lugar',
  'classes.none': 'No hay clases programadas por ahora.',
  'waitlist.need_name_email': 'Dinos tu nombre y tu correo para poder avisarte.',
  'waitlist.end_after_start': 'Elige una fecha final igual o posterior a la inicial.',
  'waitlist.could_not_add': 'No pudimos anotarte en este momento. Inténtalo de nuevo.',
  'waitlist.on_the_list': 'Estás en la lista',
  'waitlist.we_will_email': 'Te escribimos a {email} si se abre una cita de {service} entre el {from} y el {until}.',
  'waitlist.not_held': 'Los lugares no se reservan: los toma quien reserve primero, así que conviene apurarse.',
  'waitlist.back_to_times': 'Volver a las horas',
  'waitlist.tell_me': 'Avísame cuando se abra algo',
  'waitlist.from': 'Desde',
  'waitlist.until': 'Hasta',
  'waitlist.adding': 'Anotándote…',
  'waitlist.add_me': 'Anotarme en la lista',
  'waitlist.cancel': 'Cancelar',
  'waitlist.heading': 'Lista de espera',
  'waitlist.explain': 'Dinos cuándo te acomoda. Te avisamos si se abre un lugar.',
  'waitlist.submit': 'Anotarme en la lista de espera',
  'waitlist.joined': 'Estás en la lista de espera. Te avisamos si se desocupa algo.',
  'waitlist.earliest': 'Fecha más temprana',
  'waitlist.latest': 'Fecha más tardía',
  'waitlist.any_time': 'Cualquier hora del día',
  'waitlist.morning': 'Por la mañana',
  'waitlist.afternoon': 'Por la tarde',
  'waitlist.evening': 'Por la noche',

  'tiles.copied': 'Copiado',
  'tiles.copy': 'Copiar',
  'tiles.payment': 'Pago',
  'tiles.arrange_directly': 'Acuerda el pago directamente con el negocio.',
  'tiles.payment_options': 'Formas de pago',
  'tiles.due_at_appointment': 'a pagar en tu cita',
  'tiles.due_now_deposit': 'a pagar ahora como depósito',
  'tiles.due_now': 'a pagar ahora',
  'tiles.waiting_confirm': 'Esperando que el negocio confirme {amount}.',
  'tiles.nothing_outstanding': 'No queda nada por pagar. ¡Gracias!',
  'tiles.you_told_them': 'Le dijiste al negocio que enviaste {amount}. Lo confirmarán cuando aparezca en su cuenta.',
  'tiles.cancellation': 'Cancelación:',
  'tiles.refunds': 'Reembolsos:',
  'tiles.ways_to_pay': 'Formas de pagar',
  'tiles.send_with_zelle': 'Enviar con Zelle',
  'tiles.to': 'Para',
  'tiles.zelle': 'Zelle',
  'tiles.amount': 'Monto',
  'tiles.reference': 'Referencia',
  'tiles.zelle_qr_alt': 'Código QR de Zelle de {name}',
  'tiles.this_business': 'este negocio',
  'tiles.send_then_tell': 'Envíalo desde tu app del banco y luego avísale al negocio aquí abajo. Ellos lo confirman cuando llegue; Assemble no puede ver tu banco.',
  'tiles.bank_reference': 'Referencia de tu banco',
  'tiles.claim_recorded': 'Gracias: ya le avisamos al negocio y lo confirmarán.',
  'tiles.claim_error': 'No pudimos registrar eso. Inténtalo de nuevo.',
  'tiles.telling_them': 'Avisando…',
  'tiles.i_sent_it': 'Ya envié el pago',
  'tiles.taken_by_business': '{method} lo cobra el negocio. No tienes que hacer nada aquí.',
  'tiles.taken_at_appointment': '{method} lo cobra el negocio en tu cita. No tienes que hacer nada aquí.',
  'tiles.taken_by_provider': '{method} lo cobra de forma segura el procesador de pagos del negocio.',
  'manage.status_pending': 'Pendiente de confirmación',
  'manage.status_confirmed': 'Confirmada',
  'manage.status_cancelled': 'Cancelada',
  'manage.status_completed': 'Completada',
  'manage.status_no_show': 'No asististe',
  'manage.status_rescheduled': 'Reprogramada',
  'manage.missing_code': 'A este enlace le falta su código de acceso. Abre el enlace desde tu correo de confirmación.',
  'manage.not_found': 'No encontramos esta cita. Puede que el enlace esté mal o que ya no sea válido.',
  'manage.load_error': 'No pudimos cargar tu cita. Inténtalo de nuevo.',
  'manage.title': 'Tu cita',
  'manage.title_with_business': 'Tu cita · {business}',
  'manage.appointment_word': 'Cita',
  'manage.go_to_booking_page': 'Ir a la página de reservas',
  'manage.finish_checkout': 'Termina el pago seguro',
  'manage.time_reserved': 'Tu hora está reservada temporalmente. La confirmación del pago aparecerá aquí cuando el negocio lo verifique.',
  'manage.continue_checkout': 'Continuar al pago seguro',
  'manage.refresh_payment': 'Actualizar el estado del pago',
  'manage.checkout_error': 'No pudimos iniciar el pago.',
  'manage.accepted_terms': 'Términos de reserva aceptados',
  'manage.confirm_cancel': '¿Cancelar esta cita? Esto no se puede deshacer.',
  'manage.was_cancelled': 'Tu cita quedó cancelada.',
  'manage.cancel_error': 'No pudimos cancelar. Inténtalo de nuevo.',
  'manage.was_moved': 'Tu cita se cambió de hora.',
  'manage.reschedule_error': 'No pudimos cambiar la hora. Inténtalo de nuevo.',
  'manage.moved_check_email': 'Esta cita se cambió de hora. Revisa tu correo para ver el enlace de la nueva hora.',
  'manage.changes_closed': 'Los cambios en línea se cierran {notice} antes de la hora de inicio, así que esta cita ya no se puede cambiar aquí. Comunícate directamente con el negocio.',
  'manage.change_until': 'Puedes cambiar o cancelar en línea hasta el {when}.',
  'manage.reschedule_button': 'Cambiar la hora',
  'manage.working': 'Un momento…',
  'manage.choose_new_time': 'Elige una hora nueva',
  'manage.keep_current_time': 'Mantener la hora actual',
  'manage.moving': 'Cambiando…',
  'manage.confirm_new_time': 'Confirmar la hora nueva',
  'manage.other_appointments': 'Tus otras citas aquí',
  'manage.need_help': '¿Necesitas ayuda?',
  'manage.book_another': 'Reservar otra cita',
  'checkin.heading': '¿Ya llegaste?',
  'checkin.button': 'Ya llegué',
  'checkin.working': 'Registrando tu llegada…',
  'checkin.done': 'Tu llegada quedó registrada. Avisaremos al equipo.',
  'checkin.done_at': 'Llegada registrada a las {when}.',
  'checkin.too_early': 'Puedes registrar tu llegada a partir de las {when}.',
  'checkin.closed': 'El registro de llegada ya cerró para esta cita. Por favor háblanos.',
  'checkin.locating': 'Buscando tu ubicación…',
  'checkin.needs_location': 'Registra tu llegada cuando llegues: necesitamos tu ubicación para confirmar que estás aquí.',
  'checkin.location_denied': 'No pudimos leer tu ubicación. Permite el acceso e inténtalo de nuevo, o regístrate en recepción.',
  'checkin.too_far': 'Pareces estar demasiado lejos para registrar tu llegada. Inténtalo cuando llegues.',
  'checkin.front_desk': 'Por favor regístrate en recepción.',
  'checkin.error': 'No pudimos registrar tu llegada. Por favor háblanos.',
  'manage.stop_texts': 'Dejar de recibir mensajes de texto',
  'manage.texts_stopped': 'Ya no enviaremos recordatorios por texto para tus reservas actuales con este negocio.',
  'manage.preference_error': 'No pudimos actualizar tu preferencia.',
  'manage.heading': 'Tu cita',
  'manage.confirm_heading': '¿Vas a venir?',
  'manage.confirm_ask': '{business} quiere saber si vas a asistir.',
  'manage.confirm_button': 'Sí, ahí estaré',
  'manage.confirming': 'Confirmando…',
  'manage.confirmed_thanks': 'Gracias. {business} ya sabe que vas a asistir.',
  'manage.confirmed_on': 'Confirmaste esta cita el {when}.',
  'manage.confirm_error': 'No pudimos confirmar. Inténtalo de nuevo.',
  'manage.reschedule': 'Cambiar la hora',
  'manage.cancel': 'Cancelar la cita',
  'manage.cancel_confirm': '¿Cancelar esta cita?',
  'manage.cancelled': 'Esta cita está cancelada.',
  'manage.cancel_reason': 'Motivo (opcional)',
  'manage.keep': 'Mantener mi cita',
  'manage.rescheduled': 'Tu cita se cambió de hora.',
  'manage.link_expired': 'Este enlace ya no es válido. Comunícate con el negocio, por favor.',
  'manage.too_late_to_cancel': 'Ya es tarde para cancelar en línea. Comunícate directamente con el negocio.',
  'manage.status': 'Estado',
  'manage.payment': 'Pago',

  'account.sign_in_to_see': 'Inicia sesión para ver tus citas',
  'account.sign_in_free': 'Usa tu número de celular o tu correo electrónico. Es gratis.',
  'account.load_failed': 'No pudimos cargar tus citas',
  'account.try_again_moment': 'Inténtalo de nuevo en un momento.',
  'account.cancel_failed': 'No pudimos cancelar eso. Comunícate con el negocio.',
  'account.which_appointments': 'Cuáles citas',
  'account.nothing_booked': 'Todavía no hay nada reservado. Abre el enlace de reservas de un negocio para hacer una cita.',
  'account.no_past': 'Todavía no hay citas anteriores.',
  'account.booked': 'Reservada',
  'account.book_again': 'Reservar otra vez',
  'account.cancelling': 'Cancelando…',
  'account.cancel': 'Cancelar',
  'account.businesses_you_use': 'Negocios que usas',
  'account.sign_in_options': 'Tus formas de iniciar sesión',
  'account.only_your_own': 'Tu cuenta solo abre tus propias citas. Cada negocio tiene su propia página de reservas.',
  'account.contact_verified': 'Contacto verificado. Tus citas se actualizaron.',
  'account.add_verified_contact': 'Agregar un correo o celular verificado',
  'account.use_known_contact': 'Usa el contacto que tu negocio conoce para conectar tus citas existentes.',
  'account.verify_contact': 'Verificar el contacto',
  'account.try_again': 'Inténtalo de nuevo.',
  'account.connect_for_next_time': 'Conecta una cuenta para la próxima vez.',
  'account.connect': 'Conectar',
  'account.continue_with': 'Continuar con',
  'account.or_use_email': 'o usa tu correo o celular',
  'invite.error': 'No pudimos aceptar esa invitación. Inténtalo de nuevo.',
  'invite.no_longer_usable': 'Esta invitación ya no se puede usar',
  'invite.once_and_expire': 'Las invitaciones se usan una sola vez y caducan. Pídele al negocio una nueva.',
  'invite.about': 'Acerca de Book with Assemble',
  'invite.hello': 'Hola {name}',
  'invite.explain': '{business} te invitó a ver tus citas en línea, para reservar, volver a reservar y cancelar cuando quieras. Es gratis y solo tú verás tus propias citas.',
  'invite.sign_in_then_return': 'Inicia sesión y vuelve aquí',
  'invite.connecting': 'Conectando…',
  'invite.yes_connect': 'Sí, conéctame',
  'invite.only_if_you_are': 'Presiona esto solo si eres {name} y se lo pediste a {business}.',
  'signin.eyebrow': 'BIENVENIDO A BOOK WITH ASSEMBLE',
  'signin.title': 'Tu tiempo. Tus citas.',
  'signin.subtitle': 'Una sola cuenta para los negocios que te gustan.',
  'signin.link_required': 'Primero inicia sesión con un código por correo o celular y luego conecta tu proveedor desde tu cuenta.',
  'signin.social_failed': 'Ese inicio de sesión no se completó. Inténtalo de nuevo o usa un código aquí abajo.',
  'signin.enter_destination': 'Escribe tu número de celular o tu correo electrónico.',
  'signin.throttled': 'Ya se pidió varias veces. Espera unos minutos e inténtalo de nuevo.',
  'signin.generic_error': 'Algo salió mal. Inténtalo de nuevo.',
  'signin.enter_six_digits': 'Escribe los seis dígitos que te enviamos.',
  'signin.code_failed': 'Ese código no funcionó. Pide uno nuevo.',
  'signin.destination_label': 'Número de celular o correo electrónico',
  'signin.name_label': 'Tu nombre',
  'signin.name_first_time': '(solo si es tu primera vez)',
  'signin.sending': 'Enviando…',
  'signin.send_code': 'Envíame un código',
  'signin.code_label': 'Código de seis dígitos',
  'signin.checking': 'Comprobando…',
  'signin.send_another': 'Enviar otro código',
  'signin.different_number': 'Usar otro número',
  'signin.free_note': 'Reservar y administrar tus citas es gratis.',
  'signin.what_is_this': '¿Qué es Book with Assemble?',
  'signin.code_sent': 'Enviamos un código de seis dígitos a {masked}. Caduca en diez minutos.',
  'signin.sms_not_configured': 'Los mensajes de texto todavía no están activos aquí, así que no pudimos enviar un código a ese número. Usa un correo electrónico o pídele al negocio que te invite.',
  'signin.email_not_configured': 'El correo electrónico todavía no está activo aquí, así que no pudimos enviar un código. Pídele al negocio que te invite.',
  'signin.send_failed': 'No pudimos enviar el código en este momento. Inténtalo de nuevo en un momento.',
  'account.heading': 'Tus citas',
  'account.upcoming': 'Próximas',
  'account.past': 'Anteriores',
  'account.none_upcoming': 'No tienes citas próximas.',
  'account.none_past': 'Todavía no hay nada aquí.',
  'account.sign_in': 'Iniciar sesión',
  'account.sign_out': 'Cerrar sesión',
  'account.send_code': 'Enviar código',
  'account.code_label': 'Código de seis dígitos',
  'account.verify': 'Verificar',
  'account.email_or_mobile': 'Correo electrónico o celular',
  'account.please_wait': 'Un momento…',
  'account.use_another': 'Usar otro contacto',
  'account.your_businesses': 'Negocios donde reservas',

  'member.heading': 'Membresías y recordatorios para volver a reservar',
  'member.reminder_opt_in': 'Quiero recibir un recordatorio de vez en cuando por correo, cuando me toque otra visita. Es opcional; puedes darte de baja cuando quieras.',
  'member.preference_error': 'No pudimos actualizar la preferencia.',
  'member.at_business': 'Membresías en {business}',
  'member.load_error': 'No pudimos cargar las membresías.',
  'member.checkout_error': 'No pudimos iniciar el pago.',
  'member.cancel_error': 'No pudimos cancelar la membresía.',
  'member.period_ends': 'El periodo pagado termina el {date}',
  'member.will_not_renew': 'No se renovará',
  'member.resume_checkout': 'Retomar el pago seguro',
  'member.cancel': 'Cancelar la membresía',
  'member.cancel_confirm_immediate': '¿Cancelar {name}? Los beneficios terminan de inmediato.',
  'member.cancel_confirm_period_end': '¿Cancelar {name}? Los beneficios siguen hasta que termine el periodo pagado.',
  'member.price_every': '{price} cada {count} {interval}',
  'member.unlimited': 'Servicios incluidos sin límite',
  'member.included_per_period': '{count} servicios incluidos por periodo pagado',
  'member.discount_after': '{percent}% de descuento para miembros después de los servicios incluidos',
  'member.eligible_services': 'Servicios incluidos: {services}',
  'member.renews_immediate': 'Se renueva automáticamente. Puedes cancelar con efecto inmediato. Los créditos se usan dentro del periodo pagado y no se acumulan.',
  'member.renews_period_end': 'Se renueva automáticamente. Puedes cancelar al final de tu periodo pagado. Los créditos se usan dentro del periodo pagado y no se acumulan.',
  'member.accept_terms': 'Entiendo el precio recurrente y los términos de cancelación.',
  'member.join': 'Unirme con pago seguro',
  'common.optional': 'Opcional',
  'common.save': 'Guardar',
  'common.close': 'Cerrar',
  'common.loading': 'Cargando…',
  'common.duration_minutes': '{count} min',
  'common.duration_hour': '1 h',
  'common.duration_hours': '{count} h',
  'common.duration_hours_minutes': '{hours} h {minutes} min',

  // Plantillas de correo y mensaje de texto (migración 0039).
  'notify.hi': 'Hola {name}:',
  'notify.greeting_confirmation': 'Hola {name}: tu cita está confirmada.',
  'notify.greeting_reminder': 'Hola {name}: te recordamos tu próxima cita.',
  'notify.greeting_reschedule': 'Hola {name}: tu cita cambió de fecha.',
  'notify.greeting_cancellation': 'Hola {name}: tu cita fue cancelada.',
  'notify.service_with_provider': '{service} con {provider}',
  'notify.also_at_location': ' ({when} en el negocio)',
  'notify.change_it': '¿Necesitas cambiarla? {url}',
  'notify.cancellation_policy': 'Política de cancelación: {policy}',
  'notify.payment': 'Pago: {details}',
  'notify.pay_by': 'puedes pagar con {methods}',
  'notify.method_cash': 'efectivo',
  'notify.method_card_in_person': 'tarjeta en tu cita',
  'notify.method_zelle': 'Zelle a {recipient}',
  'notify.deposit_received': 'Depósito de {amount}: recibido, gracias.',
  'notify.deposit_required': 'Se requiere un depósito de {amount} para reservar esta cita.',
  'notify.questions': '¿Preguntas? {contact}',
  'notify.subject': '{summary} — {business}',
  'notify.subject_confirmation': 'Confirmada: {service} el {when}',
  'notify.subject_reminder': 'Recordatorio: {service} el {when}',
  'notify.subject_reschedule': 'Cambio de fecha: {service} ahora el {when}',
  'notify.subject_cancellation': 'Cancelada: {service} el {when}',
  'notify.review.subject': '¿Cómo te fue en {business}?',
  'notify.review.body': 'Gracias por elegir {business}. Si tienes un momento, una reseña corta ayuda a que otras personas nos encuentren:',
  'notify.waitlist.subject': 'Se desocupó un horario en {business}: {service} el {when}',
  'notify.waitlist.there': 'hola',
  'notify.waitlist.body': 'Pediste que te avisáramos cuando {business} tuviera un espacio libre. Acaba de desocuparse uno:',
  'notify.waitlist.not_held': 'No está apartado para ti: lo toma quien reserve primero, así que conviene apurarse.',
  'notify.waitlist.book_here': 'Resérvalo aquí: {url}',
  'notify.rebooking.subject': '¿Listo para otra visita a {business}?',
  'notify.rebooking.body': 'Tu última cita de {service} fue el {when}. ¿Quieres reservar de nuevo?',
  'notify.rebooking.why': 'Pediste recordatorios de {business} para volver a reservar.',
  'notify.rebooking.stop': 'Dejar de recibir estos correos: {url}',
  'notify.staff.booked': 'Un cliente reservó en línea.',
  'notify.staff.cancelled': 'Un cliente canceló en línea.',
  'notify.staff.checked_in': '{name} ya llegó y registró su llegada.',
  'notify.staff.customer': 'Cliente: {name}',
  'notify.staff.deposit_asked': 'Depósito solicitado: {amount}',
  'notify.staff.deposit_received': 'Depósito solicitado: {amount} (recibido)',
  'notify.staff.subject_new': 'Nueva reserva: {what}, {when}',
  'notify.staff.subject_cancelled': 'Cancelada: {what}, {when}',
  'notify.staff.subject_checked_in': 'Llegó: {name}, {when}',
  'notify.sms.confirmed': 'Tu cita con {business} está confirmada para el {date} a las {time}.',
  'notify.sms.reminder': 'Recordatorio: tu cita con {business} es el {date} a las {time}.',
  'notify.sms.rescheduled': 'Tu cita con {business} se movió al {date} a las {time}.',
  'notify.sms.cancelled': 'Tu cita con {business} del {date} a las {time} fue cancelada.',
  'notify.closing': '¡Hasta pronto!',
  'notify.button_confirm': 'Confirmar esta cita',
  'notify.button_details': 'Ver los detalles',
  'notify.button_book': 'Reservar una cita',
  'notify.button_cancel': 'Cancelar esta cita',
  'notify.confirm_ask': 'Confirma para que {business} sepa que vas a asistir.',
  'notify.details_heading': 'Tu cita',
  'notify.label_when': 'Cuándo',
  'notify.label_where': 'Dónde',
  'notify.label_what': 'Qué',
  'notify.plain_links': 'Si los botones no funcionan, abre este enlace:',

  'notify.invitation.subject': 'Reserva tu próxima cita con {business} en línea',
  'notify.invitation.body': '{business} ahora acepta reservas en línea. Elige la hora que mejor te venga, a cualquier hora del día, y recibe la confirmación al instante.',
  'notify.invitation.fine': 'Recibes este mensaje porque eres cliente de {business}. No tienes que hacer nada si prefieres seguir reservando como hasta ahora.',
  'notify.sms.invitation': '{business}: ya puedes reservar tu próxima cita en línea en {url}',

  'form.heading': 'Antes de tu visita',
  'form.progress': '{answered} de {total} respondidas',
  'form.required': 'Obligatorio',
  'form.optional': 'Opcional',
  'form.required_note': 'Las preguntas marcadas como obligatorias deben responderse.',
  'form.submit': 'Enviar mis respuestas',
  'form.sending': 'Enviando…',
  'form.fix_errors': 'Revisa las preguntas marcadas.',
  'form.yes': 'Sí',
  'form.no': 'No',
  'form.choose': 'Elige una',
  'form.choose_any': 'Elige las que correspondan',
  'form.signature_label': 'Escribe tu nombre completo',
  'form.initials_label': 'Escribe tus iniciales',
  'form.attach': 'Adjuntar un archivo',
  'form.attaching': 'Subiendo…',
  'form.attached': 'Adjuntado: {name}',
  'form.attach_hint': 'JPEG, PNG, WebP o PDF, hasta 5 MB. Se guarda de forma privada.',
  'form.confirm_unchanged': 'Nada ha cambiado desde la última vez que completé este formulario.',
  'form.confirm_last_completed': 'Lo completaste por última vez el {date}.',
  'form.confirm_button': 'Confirmar que nada ha cambiado',
  'form.done_title': 'Gracias: ya tenemos tus respuestas',
  'form.done_body': 'No hay nada más que hacer. Puedes cerrar esta página.',
  'form.expired_title': 'Este enlace ha caducado',
  'form.expired_body': 'Pídele al negocio que te envíe uno nuevo.',
  'form.completed_title': 'Este formulario ya está completo',
  'form.completed_body': 'Gracias: no hay nada más que hacer.',
  'form.not_found_title': 'No encontramos este formulario',
  'form.not_found_body': 'Puede que el enlace no sea correcto o que lo haya reemplazado uno más nuevo.',
  'form.expires_on': 'Este enlace funciona hasta el {date}.',
  'form.privacy_heading': 'Tu privacidad',
  'form.for_appointment': 'Para tu cita de {service} el {when}.',

  // Solicitudes de servicio (0053).
  'request.heading': 'Solicitar un servicio',
  'request.intro': 'Cuéntanos qué necesitas y cómo contactarte. No hace falta cita — nos pondremos en contacto.',
  'request.start': 'Empezar una solicitud',
  'request.about_you': 'Sobre ti',
  'request.name': 'Tu nombre',
  'request.phone': 'Número de teléfono',
  'request.email': 'Correo electrónico',
  'request.contact_note': 'Necesitamos un número de teléfono o un correo electrónico para contactarte.',
  'request.submit': 'Enviar mi solicitud',
  'request.sending': 'Enviando…',
  'request.done_title': 'Solicitud enviada',
  'request.done_body': '{name} recibió tu solicitud y se pondrá en contacto contigo.',
  'request.another': 'Enviar otra solicitud',
  'request.not_available': 'Este negocio no está recibiendo solicitudes en línea por ahora.',
  'notify.request.subject_received': 'Recibimos tu solicitud — {business}',
  'notify.request.received_body': 'Gracias — {business} recibió tu solicitud y se pondrá en contacto contigo.',
  'notify.request.subject_update': 'Tu solicitud con {business}: {status}',
  'notify.request.update_body': 'Tu solicitud con {business} ahora está {status}.',
  'notify.request.completed_body': 'Tu solicitud con {business} está completada.',
  'notify.request.cancelled_body': 'Tu solicitud con {business} fue cancelada.',
  'notify.request.label_request': 'Tu solicitud',
  'notify.request.label_address': 'Dirección del servicio',
  'notify.request.label_needed': 'Para cuándo',
  'notify.request.sms_received': '{business}: recibimos tu solicitud y nos pondremos en contacto.',
  'notify.request.sms_update': '{business}: tu solicitud ahora está {status}.',
  'notify.request.status_new': 'recibida',
  'notify.request.status_contacted': 'en contacto',
  'notify.request.status_in_progress': 'en curso',
  'notify.request.status_waiting': 'en espera de una respuesta',
  'notify.request.status_completed': 'completada',
  'notify.request.status_cancelled': 'cancelada',
  'notify.staff.request_subject': 'Nueva solicitud de servicio: {name}',
  'notify.staff.request_body': '{name} envió una solicitud de servicio desde tu página.',

  'kiosk.title': '{name} | Quiosco',
  'kiosk.pair_title': 'Configurar este quiosco',
  'kiosk.pair_hint': 'Pide a un gerente el código de seis dígitos de la consola e introdúcelo aquí.',
  'kiosk.pair_code': 'Código de vinculación',
  'kiosk.pair_button': 'Conectar',
  'kiosk.pair_failed': 'Ese código no es válido. Pide uno nuevo a un gerente.',
  'kiosk.off_title': 'El quiosco está apagado',
  'kiosk.off_message': 'Por favor, habla con el equipo en el mostrador.',
  'kiosk.reconnect': 'Este quiosco debe configurarse de nuevo. Pide un código a un gerente.',
  'kiosk.welcome': 'Bienvenido',
  'kiosk.welcome_to': 'Bienvenido a {name}',
  'kiosk.tap_to_start': 'Toca para empezar',
  'kiosk.have_appointment': 'Tengo una cita',
  'kiosk.walk_in': 'Sin cita',
  'kiosk.start_order': 'Hacer un pedido',
  'kiosk.order_at_counter': 'Por favor, pide en el mostrador.',
  'kiosk.start_over': 'Empezar de nuevo',
  'kiosk.back': 'Atrás',
  'kiosk.next': 'Siguiente',
  'kiosk.skip': 'Omitir',
  'kiosk.done': 'Listo',
  'kiosk.working': 'Un momento…',
  'kiosk.find_title': 'Busca tu cita',
  'kiosk.find_hint': 'Escribe el número de celular o el nombre con el que se reservó la cita.',
  'kiosk.phone': 'Número de celular',
  'kiosk.name': 'Tu nombre',
  'kiosk.find_button': 'Buscar mi cita',
  'kiosk.not_found_title': 'No encontramos una cita',
  'kiosk.not_found_message': 'Revisa el número o el nombre, o habla con el equipo en el mostrador.',
  'kiosk.matches_title': '¿Eres tú?',
  'kiosk.check_in': 'Registrar llegada',
  'kiosk.too_early': 'Un poco temprano: el registro abre a las {time}.',
  'kiosk.already_checked_in': 'Llegada ya registrada',
  'kiosk.closed': 'Por favor, habla con el equipo en el mostrador.',
  'kiosk.with_person': 'con {name}',
  'kiosk.checked_in_title': 'Llegada registrada',
  'kiosk.checked_in_message': 'Gracias, {name}. Toma asiento; el equipo ya sabe que llegaste.',
  'kiosk.walk_name_title': '¿Cómo te llamas?',
  'kiosk.walk_phone_title': 'Tu número de celular (opcional)',
  'kiosk.walk_phone_hint': 'Para que el equipo pueda contactarte si hace falta.',
  'kiosk.walk_service_title': '¿Qué necesitas hoy?',
  'kiosk.walk_service_any': 'Aún no lo sé',
  'kiosk.walk_provider_title': '¿Con alguien en particular?',
  'kiosk.walk_provider_any': 'El primero disponible',
  'kiosk.walk_confirm_title': '¿Unirte a la fila?',
  'kiosk.walk_join': 'Unirme a la fila',
  'kiosk.walk_done_title': 'Estás en la fila',
  'kiosk.walk_position': 'Eres el número {position} en la fila.',
  'kiosk.walk_next': 'Eres el siguiente.',
  'kiosk.walk_ahead': '{count} antes que tú.',
  'kiosk.walk_done_message': 'Toma asiento; el equipo dirá tu nombre.',
  'kiosk.minutes': '{count} min',
  'kiosk.menu_title': 'Menú',
  'kiosk.menu_all': 'Todo',
  'kiosk.sold_out': 'Agotado',
  'kiosk.add': 'Agregar',
  'kiosk.add_to_order': 'Agregar al pedido',
  'kiosk.quantity': 'Cantidad',
  'kiosk.required': 'Obligatorio',
  'kiosk.optional': 'Opcional',
  'kiosk.choose_one': 'Elige una opción',
  'kiosk.yes': 'Sí',
  'kiosk.no': 'No',
  'kiosk.view_order': 'Ver pedido',
  'kiosk.your_order': 'Tu pedido',
  'kiosk.empty_order': 'Todavía no hay nada en tu pedido.',
  'kiosk.order_notes': '¿Algo que debamos saber?',
  'kiosk.order_notes_hint': 'Alergias, sin cebolla, servilletas extra…',
  'kiosk.remove': 'Quitar',
  'kiosk.total': 'Total',
  'kiosk.continue': 'Continuar',
  'kiosk.order_name_title': 'Nombre para el pedido',
  'kiosk.order_phone_title': 'Número de celular (opcional)',
  'kiosk.place_order': 'Hacer el pedido',
  'kiosk.pay_at_counter': 'Paga en el mostrador',
  'kiosk.order_done_title': 'Pedido realizado',
  'kiosk.order_number': 'Tu número de pedido',
  'kiosk.order_done_message': 'Paga en el mostrador. Te llamaremos por tu nombre cuando esté listo.',
  'kiosk.returning_in': 'Volviendo al inicio en {seconds}s',
  'kiosk.error_generic': 'Algo salió mal. Inténtalo de nuevo o habla con el equipo.',
  'kiosk.each': 'c/u',
};

export const MESSAGES: Record<Locale, Partial<Record<MessageKey, string>>> = { en: EN, es: ES };

/** Every key English defines — what an offered language must cover. */
export function messageKeys(): MessageKey[] {
  return Object.keys(EN) as MessageKey[];
}

/**
 * One string in one language. `{name}` placeholders take the values
 * given; a placeholder with no value is left as it stands rather than
 * printing "undefined".
 */
export function t(locale: Locale, key: MessageKey, vars: Record<string, string | number> = {}): string {
  const template = MESSAGES[locale]?.[key] ?? EN[key];
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.hasOwn(vars, name) ? String(vars[name]) : whole);
}

/** A `t` bound to one locale, which is what a component wants. */
export type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

export function translator(locale: Locale): Translate {
  return (key, vars) => t(locale, key, vars);
}

/**
 * Which language to use, in the order a person's own choice should win:
 * their explicit choice, then the business's own default for its page,
 * then what their browser asks for, then English.
 *
 * `offeredOnly` is the honesty gate: a language whose strings are not
 * complete is never selected, however loudly anything asks for it.
 */
export function negotiateLocale(
  { chosen, business, accept }: { chosen?: string | null; business?: string | null; accept?: string | null },
  offeredOnly = true,
): Locale {
  const allow = (locale: Locale) => (offeredOnly ? localeIsOffered(locale) : true);
  if (isLocale(chosen) && allow(chosen)) return chosen;
  if (isLocale(business) && allow(business)) return business;
  for (const tag of parseAcceptLanguage(accept)) {
    const base = tag.split('-')[0]!.toLowerCase();
    if (isLocale(base) && allow(base)) return base;
  }
  return DEFAULT_LOCALE;
}

/** The language tags of an Accept-Language header, best quality first. */
export function parseAcceptLanguage(header?: string | null): string[] {
  if (!header) return [];
  return header
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.trim().split(';');
      const q = params.map((p) => p.trim()).find((p) => p.startsWith('q='));
      const quality = q ? Number.parseFloat(q.slice(2)) : 1;
      return { tag: (tag ?? '').trim(), quality: Number.isFinite(quality) ? quality : 0 };
    })
    .filter((entry) => entry.tag.length > 0 && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality)
    .map((entry) => entry.tag);
}
